import { useState } from "react";
import { Check, RotateCcw } from "lucide-react";
import { LAUNCH_STEPS } from "@/content/launchChecklist";

const STORAGE_KEY = "clarus-launch-checklist";

function loadState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

export default function LaunchChecklist() {
  const [done, setDone] = useState(loadState);
  const completed = LAUNCH_STEPS.filter((s) => done[s.id]).length;

  const toggle = (id) => {
    const next = { ...done, [id]: !done[id] };
    setDone(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const reset = () => {
    setDone({});
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <div data-testid="launch-checklist" className="max-w-3xl">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-navy/60" data-testid="launch-progress">
          {completed} of {LAUNCH_STEPS.length} done
        </p>
        <button
          data-testid="launch-reset-button"
          onClick={reset}
          className="inline-flex items-center gap-2 text-sm font-semibold text-navy/50 hover:text-navy transition-colors"
        >
          <RotateCcw className="w-4 h-4" aria-hidden="true" /> Reset
        </button>
      </div>
      <div className="mt-4 h-2 rounded-full bg-border overflow-hidden">
        <div
          className="h-full bg-terracotta transition-all duration-500"
          style={{ width: `${(completed / LAUNCH_STEPS.length) * 100}%` }}
        />
      </div>
      <ol className="mt-8 space-y-4">
        {LAUNCH_STEPS.map((step) => (
          <li
            key={step.id}
            data-testid={`launch-step-${step.id}`}
            className={`rounded-2xl border p-6 transition-colors duration-200 ${
              done[step.id] ? "bg-terracotta-light border-terracotta/30" : "bg-white border-border"
            }`}
          >
            <label className="flex items-start gap-4 cursor-pointer">
              <input
                type="checkbox"
                data-testid={`launch-check-${step.id}`}
                checked={!!done[step.id]}
                onChange={() => toggle(step.id)}
                className="mt-1 w-5 h-5 accent-terracotta"
              />
              <span>
                <span className={`block text-base font-bold ${done[step.id] ? "text-navy/50 line-through" : "text-navy"}`}>
                  {step.title}
                </span>
                <span className="mt-1.5 block text-sm text-navy/70 leading-relaxed">{step.detail}</span>
              </span>
            </label>
          </li>
        ))}
      </ol>
      {completed === LAUNCH_STEPS.length && (
        <div className="mt-6 rounded-2xl bg-navy text-white p-6 flex items-center gap-4" data-testid="launch-complete">
          <Check className="w-6 h-6 text-terracotta shrink-0" aria-hidden="true" />
          <p className="text-sm font-semibold">All done - you're live. Watch the leads come in.</p>
        </div>
      )}
    </div>
  );
}
